import React, { useEffect, useState } from 'react';
import { useToast } from 'use-toast-mui';
import { MenuItem, Menu, FormControlLabel, Checkbox, Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import CommonModalLayout from '../../../form-components/CommonModalLayout';
import CommonModalButton from '../../../form-components/CommonModalButton';
import {
  ModalBody,
  LockableSection,
  SameAsStoreBanner,
  BannerTextRow,
  BannerTitle,
  BannerHint,
  TealSwitch,
  DayRow,
  DayLabel,
  OpenToggleWrap,
  OpenToggleText,
  TimeSelect,
  Colon,
  Tilde,
  ApplyLabelText,
  NextDayBadge,
  HolidaySection,
  AccordionHeader,
  AccordionTitle,
  AccordionContent,
  HolidayListPane,
  AddHolidayButton,
  HolidayChips,
  HolidayChip,
  HolidayChipText,
  HolidayChipRemove,
  CalendarPane,
  CalendarHeader,
  CalendarNavArrow,
  CalendarMonthLabel,
  WeekDayHeaderRow,
  WeekDayHeaderCell,
  CalendarGrid,
  CalendarDayCell,
} from './EntranceTimeModalStyled';

// ============================================
// 상수
// ============================================
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));
const WEEKDAY_HEADERS = ['일', '월', '화', '수', '목', '금', '토'];

// "HH:MM" → 분. 종료 ≤ 시작이면 자정을 넘기는 "익일" 종료 (IoT 주간 스케줄과 같은 규칙, 서버도 동일 해석)
const toMinutes = time => {
  const [hour = '0', minute = '0'] = (time || '').split(':');
  return parseInt(hour, 10) * 60 + parseInt(minute, 10);
};
export const isOvernight = (openTime, closeTime) => toMinutes(closeTime) <= toMinutes(openTime);

// 휴무일 최대 선택 개수
const MAX_HOLIDAYS = 30;

const DAY_LABELS = {
  MONDAY: '월요일',
  TUESDAY: '화요일',
  WEDNESDAY: '수요일',
  THURSDAY: '목요일',
  FRIDAY: '금요일',
  SATURDAY: '토요일',
  SUNDAY: '일요일',
};

// 입장시간 기본값 (매장 영업시간 prop이 없을 때)
const DEFAULT_WORK_HOURS = [
  { dayOfWeek: 'MONDAY', openTime: '09:00', closeTime: '23:00', open: true },
  { dayOfWeek: 'TUESDAY', openTime: '09:00', closeTime: '23:00', open: true },
  { dayOfWeek: 'WEDNESDAY', openTime: '09:00', closeTime: '23:00', open: true },
  { dayOfWeek: 'THURSDAY', openTime: '09:00', closeTime: '23:00', open: true },
  { dayOfWeek: 'FRIDAY', openTime: '09:00', closeTime: '23:00', open: true },
  { dayOfWeek: 'SATURDAY', openTime: '09:00', closeTime: '23:00', open: true },
  { dayOfWeek: 'SUNDAY', openTime: '09:00', closeTime: '23:00', open: false },
];

// 공휴일 목록 (양력 기준)
const PUBLIC_HOLIDAYS = [
  { name: '신정', month: 1, day: 1 },
  { name: '삼일절', month: 3, day: 1 },
  { name: '어린이날', month: 5, day: 5 },
  { name: '현충일', month: 6, day: 6 },
  { name: '광복절', month: 8, day: 15 },
  { name: '개천절', month: 10, day: 3 },
  { name: '한글날', month: 10, day: 9 },
  { name: '크리스마스', month: 12, day: 25 },
];

// ============================================
// 달력 유틸
// ============================================
const getCalendarDays = date => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const days = [];
  const startDayOfWeek = firstDay.getDay();

  // 이전 달 채우기
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    days.push({ date: new Date(year, month - 1, prevMonthLastDay - i), isCurrentMonth: false });
  }
  // 현재 달
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  }
  // 다음 달 (6주 채우기)
  const remainingDays = 42 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
  }
  return days;
};

const formatDateString = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 저장된 입장시간을 월~일 순서로 정렬하고, 빠진 요일은 기본값으로 채운다.
const orderByWeekday = hours =>
  DEFAULT_WORK_HOURS.map(defaultHour => {
    const saved = hours.find(item => item.dayOfWeek === defaultHour.dayOfWeek);
    return saved ? { ...saved } : { ...defaultHour };
  });

// ============================================
// 입장시간 설정 모달
// - sameAsStore(매장 영업시간과 동일) ON이면 요일별/휴무일 입력을 잠근다.
// - initialEntranceTime(저장된 설정)이 있으면 그 값으로 초기화, 없으면 매장 영업시간 fallback.
// ============================================
const EntranceTimeModal = ({
  open,
  onClose,
  workHours,
  initialHolidays,
  initialEntranceTime,
  onSave,
  // 출입문(문별 입장시간) 재사용 옵션 — 미지정 시 기존 지점 입장시간 화면 그대로.
  title = '입장시간 설정',
  sameLabel = '매장 영업시간과 동일',
  sameHint = '*매장의 휴무일 설정도 동일하게 적용됩니다.',
  hideHolidays = false,
}) => {
  const toast = useToast();
  const [sameAsStore, setSameAsStore] = useState(true);
  const [entranceHours, setEntranceHours] = useState(DEFAULT_WORK_HOURS);
  const [applyWeekday, setApplyWeekday] = useState(false);
  const [applyWeekend, setApplyWeekend] = useState(false);

  const [holidayExpanded, setHolidayExpanded] = useState(false);
  const [selectedHolidays, setSelectedHolidays] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [holidayMenuAnchor, setHolidayMenuAnchor] = useState(null);

  // 모달이 열릴 때 저장된 입장시간(없으면 매장 영업시간)으로 초기화
  useEffect(() => {
    if (!open) return;
    const savedHours = initialEntranceTime?.entranceHours;
    // sameAsStore:false 요청(별도 설정 프리셋)은 저장된 시간이 아직 없어도 존중한다.
    const forceSeparate = initialEntranceTime?.sameAsStore === false;
    if (savedHours?.length > 0) {
      setEntranceHours(orderByWeekday(savedHours));
    } else {
      setEntranceHours(workHours?.length ? workHours.map(item => ({ ...item })) : DEFAULT_WORK_HOURS);
    }
    setSameAsStore(!forceSeparate);
    setApplyWeekday(false);
    setApplyWeekend(false);
    // 영업시간 화면에서 로드한 휴무일을 그대로 보여줘 두 화면을 동기화
    const holidays = Array.isArray(initialHolidays) ? [...initialHolidays].sort() : [];
    setSelectedHolidays(holidays);
    // 기존 휴무일이 있으면 아코디언을 펼쳐서 바로 보이게 (sameAsStore 잠금 시에도 표시됨)
    setHolidayExpanded(holidays.length > 0);
    setHolidayMenuAnchor(null);
  }, [open, workHours, initialHolidays, initialEntranceTime]);

  // ============================================
  // 요일별 영업시간 핸들러
  // ============================================
  const handleToggleOpen = (day, isOpen) => {
    setEntranceHours(prev => prev.map(item => (item.dayOfWeek === day ? { ...item, open: isOpen } : item)));
  };

  const handleTimeChange = (day, field, value) => {
    setEntranceHours(prev => prev.map(item => (item.dayOfWeek === day ? { ...item, [field]: value } : item)));
  };

  // 평일(화~금)에 월요일 값 일괄 적용
  const handleApplyWeekday = checked => {
    setApplyWeekday(checked);
    if (!checked) return;
    const monday = entranceHours.find(item => item.dayOfWeek === 'MONDAY');
    if (!monday) return;
    const weekdays = ['TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
    setEntranceHours(prev =>
      prev.map(item =>
        weekdays.includes(item.dayOfWeek)
          ? { ...item, openTime: monday.openTime, closeTime: monday.closeTime, open: monday.open }
          : item,
      ),
    );
  };

  // 일요일에 토요일 값 일괄 적용
  const handleApplyWeekend = checked => {
    setApplyWeekend(checked);
    if (!checked) return;
    const saturday = entranceHours.find(item => item.dayOfWeek === 'SATURDAY');
    if (!saturday) return;
    setEntranceHours(prev =>
      prev.map(item =>
        item.dayOfWeek === 'SUNDAY'
          ? { ...item, openTime: saturday.openTime, closeTime: saturday.closeTime, open: saturday.open }
          : item,
      ),
    );
  };

  // ============================================
  // 휴무일 핸들러
  // ============================================
  const handleDateClick = date => {
    const dateString = formatDateString(date);
    if (selectedHolidays.includes(dateString)) {
      setSelectedHolidays(prev => prev.filter(d => d !== dateString));
      return;
    }
    if (selectedHolidays.length >= MAX_HOLIDAYS) {
      toast.warning(`휴무일은 최대 ${MAX_HOLIDAYS}개까지 선택할 수 있습니다.`);
      return;
    }
    setSelectedHolidays(prev => [...prev, dateString].sort());
  };

  const removeHoliday = dateString => {
    setSelectedHolidays(prev => prev.filter(d => d !== dateString));
  };

  const addPublicHoliday = holiday => {
    const year = calendarDate.getFullYear();
    const dateString = `${year}-${String(holiday.month).padStart(2, '0')}-${String(holiday.day).padStart(2, '0')}`;
    setHolidayMenuAnchor(null);
    if (selectedHolidays.includes(dateString)) return;
    if (selectedHolidays.length >= MAX_HOLIDAYS) {
      toast.warning(`휴무일은 최대 ${MAX_HOLIDAYS}개까지 선택할 수 있습니다.`);
      return;
    }
    setSelectedHolidays(prev => [...prev, dateString].sort());
  };

  const addAllPublicHolidays = () => {
    const year = calendarDate.getFullYear();
    const newHolidays = PUBLIC_HOLIDAYS.map(
      h => `${year}-${String(h.month).padStart(2, '0')}-${String(h.day).padStart(2, '0')}`,
    );
    setHolidayMenuAnchor(null);
    const merged = [...new Set([...selectedHolidays, ...newHolidays])].sort();
    if (merged.length > MAX_HOLIDAYS) {
      toast.warning(`휴무일은 최대 ${MAX_HOLIDAYS}개까지 선택할 수 있습니다.`);
      setSelectedHolidays(merged.slice(0, MAX_HOLIDAYS));
      return;
    }
    setSelectedHolidays(merged);
  };

  const goToPrevMonth = () => setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const goToNextMonth = () => setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  const handleSave = () => {
    onSave?.({ sameAsStore, entranceHours, selectedHolidays });
    onClose();
  };

  const calendarDays = getCalendarDays(calendarDate);

  return (
    <CommonModalLayout
      open={open}
      onClose={onClose}
      title={title}
      width={980}
      footer={
        <>
          <CommonModalButton variant="cancel" onClick={onClose}>
            취소
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleSave}>
            저장
          </CommonModalButton>
        </>
      }>
      <ModalBody>
        {/* 매장 영업시간과 동일 토글 */}
        <SameAsStoreBanner>
          <BannerTextRow>
            <BannerTitle>{sameLabel}</BannerTitle>
            <BannerHint>{sameHint}</BannerHint>
          </BannerTextRow>
          <TealSwitch checked={sameAsStore} onChange={e => setSameAsStore(e.target.checked)} />
        </SameAsStoreBanner>

        {/* 요일별 영업시간 + 휴무일 (동일 토글 ON이면 잠금) */}
        <LockableSection locked={sameAsStore}>
          {entranceHours.map(workHour => (
            <DayRow key={workHour.dayOfWeek}>
              <DayLabel>{DAY_LABELS[workHour.dayOfWeek]}</DayLabel>

              {/* 영업/휴무 토글 */}
              <OpenToggleWrap>
                <OpenToggleText isOpen={workHour.open}>{workHour.open ? '영업' : '휴무'}</OpenToggleText>
                <TealSwitch
                  checked={workHour.open}
                  onChange={e => handleToggleOpen(workHour.dayOfWeek, e.target.checked)}
                />
              </OpenToggleWrap>

              {/* 시작 시간 */}
              <TimeSelect
                value={workHour.openTime?.split(':')[0] || '09'}
                onChange={e =>
                  handleTimeChange(
                    workHour.dayOfWeek,
                    'openTime',
                    `${e.target.value}:${workHour.openTime?.split(':')[1] || '00'}`,
                  )
                }
                disabled={!workHour.open}
                size="small"
                isOpen={workHour.open}
                IconComponent={KeyboardArrowDownIcon}>
                {HOUR_OPTIONS.map(hour => (
                  <MenuItem key={hour} value={hour}>
                    {hour}
                  </MenuItem>
                ))}
              </TimeSelect>
              <Colon>:</Colon>
              <TimeSelect
                value={workHour.openTime?.split(':')[1] || '00'}
                onChange={e =>
                  handleTimeChange(
                    workHour.dayOfWeek,
                    'openTime',
                    `${workHour.openTime?.split(':')[0] || '09'}:${e.target.value}`,
                  )
                }
                disabled={!workHour.open}
                size="small"
                isOpen={workHour.open}
                IconComponent={KeyboardArrowDownIcon}>
                {MINUTE_OPTIONS.map(min => (
                  <MenuItem key={min} value={min}>
                    {min}
                  </MenuItem>
                ))}
              </TimeSelect>

              <Tilde>~</Tilde>

              {/* 종료 시간 */}
              <TimeSelect
                value={workHour.closeTime?.split(':')[0] || '23'}
                onChange={e =>
                  handleTimeChange(
                    workHour.dayOfWeek,
                    'closeTime',
                    `${e.target.value}:${workHour.closeTime?.split(':')[1] || '00'}`,
                  )
                }
                disabled={!workHour.open}
                size="small"
                isOpen={workHour.open}
                IconComponent={KeyboardArrowDownIcon}>
                {HOUR_OPTIONS.map(hour => (
                  <MenuItem key={hour} value={hour}>
                    {hour}
                  </MenuItem>
                ))}
              </TimeSelect>
              <Colon>:</Colon>
              <TimeSelect
                value={workHour.closeTime?.split(':')[1] || '00'}
                onChange={e =>
                  handleTimeChange(
                    workHour.dayOfWeek,
                    'closeTime',
                    `${workHour.closeTime?.split(':')[0] || '23'}:${e.target.value}`,
                  )
                }
                disabled={!workHour.open}
                size="small"
                isOpen={workHour.open}
                IconComponent={KeyboardArrowDownIcon}>
                {MINUTE_OPTIONS.map(min => (
                  <MenuItem key={min} value={min}>
                    {min}
                  </MenuItem>
                ))}
              </TimeSelect>
              {workHour.open && isOvernight(workHour.openTime, workHour.closeTime) && (
                <NextDayBadge>익일</NextDayBadge>
              )}

              {/* 일괄 적용 체크박스 */}
              {workHour.dayOfWeek === 'MONDAY' && (
                <FormControlLabel
                  sx={{ ml: '16px' }}
                  control={
                    <Checkbox
                      checked={applyWeekday}
                      onChange={e => handleApplyWeekday(e.target.checked)}
                      sx={{ color: '#0F7F85', '&.Mui-checked': { color: '#0F7F85' } }}
                    />
                  }
                  label={<ApplyLabelText>평일(월-금) 일괄 적용</ApplyLabelText>}
                />
              )}
              {workHour.dayOfWeek === 'SATURDAY' && (
                <FormControlLabel
                  sx={{ ml: '16px' }}
                  control={
                    <Checkbox
                      checked={applyWeekend}
                      onChange={e => handleApplyWeekend(e.target.checked)}
                      sx={{ color: '#0F7F85', '&.Mui-checked': { color: '#0F7F85' } }}
                    />
                  }
                  label={<ApplyLabelText>주말(토, 일) 일괄 적용</ApplyLabelText>}
                />
              )}
            </DayRow>
          ))}

          {/* 휴무일 설정 */}
          {!hideHolidays && (
          <HolidaySection>
            <AccordionHeader expanded={holidayExpanded} onClick={() => setHolidayExpanded(prev => !prev)}>
              <AccordionTitle>휴무일</AccordionTitle>
              <KeyboardArrowDownIcon
                sx={{
                  transform: holidayExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  color: '#666',
                }}
              />
            </AccordionHeader>

            {holidayExpanded && (
              <AccordionContent>
                {/* 왼쪽: 선택된 휴무일 목록 */}
                <HolidayListPane>
                  <Box>
                    <AddHolidayButton
                      variant="outlined"
                      size="small"
                      onClick={e => setHolidayMenuAnchor(e.currentTarget)}>
                      공휴일 추가
                    </AddHolidayButton>
                    <Menu
                      anchorEl={holidayMenuAnchor}
                      open={Boolean(holidayMenuAnchor)}
                      onClose={() => setHolidayMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                      {(() => {
                        const year = calendarDate.getFullYear();
                        const addedCount = PUBLIC_HOLIDAYS.filter(h => {
                          const ds = `${year}-${String(h.month).padStart(2, '0')}-${String(h.day).padStart(2, '0')}`;
                          return selectedHolidays.includes(ds);
                        }).length;
                        const allAdded = addedCount === PUBLIC_HOLIDAYS.length;
                        return (
                          <MenuItem
                            onClick={addAllPublicHolidays}
                            sx={{
                              fontSize: '13px',
                              fontWeight: 500,
                              color: '#0F7F85',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              backgroundColor: allAdded ? 'rgba(15, 127, 133, 0.08)' : 'inherit',
                            }}>
                            <span>전체 공휴일 추가 ({year}년)</span>
                            <Box component="span" sx={{ fontSize: '12px', color: allAdded ? '#0F7F85' : '#999', ml: 1 }}>
                              {addedCount}/{PUBLIC_HOLIDAYS.length}
                            </Box>
                          </MenuItem>
                        );
                      })()}
                      <Box sx={{ borderTop: '1px solid #eee', my: '4px' }} />
                      {PUBLIC_HOLIDAYS.map(holiday => {
                        const year = calendarDate.getFullYear();
                        const dateString = `${year}-${String(holiday.month).padStart(2, '0')}-${String(holiday.day).padStart(2, '0')}`;
                        const isAdded = selectedHolidays.includes(dateString);
                        return (
                          <MenuItem
                            key={`${holiday.month}-${holiday.day}`}
                            onClick={() => addPublicHoliday(holiday)}
                            sx={{
                              fontSize: '13px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              color: isAdded ? '#0F7F85' : 'inherit',
                              backgroundColor: isAdded ? 'rgba(15, 127, 133, 0.08)' : 'inherit',
                            }}>
                            <span>
                              {holiday.name} ({holiday.month}.{holiday.day})
                            </span>
                            {isAdded && <CheckIcon sx={{ fontSize: '16px', ml: 1, color: '#0F7F85' }} />}
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </Box>

                  <HolidayChips>
                    {selectedHolidays.map(dateString => (
                      <HolidayChip key={dateString}>
                        <HolidayChipText>{dateString}</HolidayChipText>
                        <HolidayChipRemove onClick={() => removeHoliday(dateString)}>×</HolidayChipRemove>
                      </HolidayChip>
                    ))}
                  </HolidayChips>
                </HolidayListPane>

                {/* 오른쪽: 달력 */}
                <CalendarPane>
                  <CalendarHeader>
                    <CalendarNavArrow onClick={goToPrevMonth}>{'<'}</CalendarNavArrow>
                    <CalendarMonthLabel>
                      {calendarDate.getFullYear()}. {calendarDate.getMonth() + 1}
                    </CalendarMonthLabel>
                    <CalendarNavArrow onClick={goToNextMonth}>{'>'}</CalendarNavArrow>
                  </CalendarHeader>

                  <WeekDayHeaderRow>
                    {WEEKDAY_HEADERS.map((day, index) => (
                      <WeekDayHeaderCell key={day} dayIndex={index}>
                        {day}
                      </WeekDayHeaderCell>
                    ))}
                  </WeekDayHeaderRow>

                  <CalendarGrid>
                    {calendarDays.map((dayInfo, index) => {
                      const dateString = formatDateString(dayInfo.date);
                      const dayOfWeek = dayInfo.date.getDay();
                      return (
                        <CalendarDayCell
                          key={index}
                          onClick={() => handleDateClick(dayInfo.date)}
                          isSelected={selectedHolidays.includes(dateString)}
                          isCurrentMonth={dayInfo.isCurrentMonth}
                          isSunday={dayOfWeek === 0}
                          isSaturday={dayOfWeek === 6}>
                          {dayInfo.date.getDate()}
                        </CalendarDayCell>
                      );
                    })}
                  </CalendarGrid>
                </CalendarPane>
              </AccordionContent>
            )}
          </HolidaySection>
          )}
        </LockableSection>
      </ModalBody>
    </CommonModalLayout>
  );
};

export default EntranceTimeModal;
